import type { SessionSummary } from '@school-erp/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../../api/http';
import { Notice, errorMessage, primaryButton, secondaryButton } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';

interface MfaStatus {
  available: boolean;
  enabled: boolean;
  recoveryCodesLeft: number;
}
type Message = { kind: 'ok' | 'error'; text: string } | null;

/** The signed-in person's own security: two-step sign-in, their password, and the devices they are signed in on. */
export function AccountPage() {
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-bold">{t('account.title')}</h1>
      <TwoStep />
      <ChangePassword />
      <Sessions />
    </div>
  );
}

const card = 'space-y-3 rounded-lg border border-slate-200 bg-white p-4';

function TwoStep() {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['account', 'mfa'],
    queryFn: () => api<MfaStatus>('/auth/mfa'),
  });
  const [message, setMessage] = useState<Message>(null);
  const [step, setStep] = useState<
    'idle' | 'password' | 'code' | 'recovery' | 'replace' | 'disable'
  >('idle');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [recoveryField, setRecoveryField] = useState('');
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [codes, setCodes] = useState<string[]>([]);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['account', 'mfa'] });
  const reset = () => {
    setStep('idle');
    setPassword('');
    setCode('');
    setRecoveryField('');
    setSetup(null);
  };
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });

  const begin = useMutation({
    mutationFn: () =>
      api<{ secret: string; otpauthUri: string }>('/auth/mfa/setup', {
        method: 'POST',
        body: { password },
      }),
    onSuccess: (data) => {
      setMessage(null);
      setSetup(data);
      setPassword('');
      setStep('code');
    },
    onError: fail,
  });
  const enable = useMutation({
    mutationFn: () =>
      api<{ recoveryCodes: string[] }>('/auth/mfa/enable', { method: 'POST', body: { code } }),
    onSuccess: (data) => {
      setCodes(data.recoveryCodes);
      setSetup(null);
      setCode('');
      setStep('recovery');
      setMessage({ kind: 'ok', text: t('account.mfa.saved') });
      refresh();
    },
    onError: fail,
  });
  const replace = useMutation({
    mutationFn: () =>
      api<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes', {
        method: 'POST',
        body: { password, code },
      }),
    onSuccess: (data) => {
      setCodes(data.recoveryCodes);
      setPassword('');
      setCode('');
      setStep('recovery');
      setMessage(null);
      refresh();
    },
    onError: fail,
  });
  const disable = useMutation({
    mutationFn: () =>
      api('/auth/mfa/disable', {
        method: 'POST',
        body: recoveryField.trim()
          ? { password, recoveryCode: recoveryField.trim() }
          : { password, code: code.trim() },
      }),
    onSuccess: () => {
      reset();
      setMessage({ kind: 'ok', text: t('account.mfa.disabled') });
      refresh();
    },
    onError: fail,
  });

  if (status.isPending) return <p role="status">{t('common.loading')}</p>;
  if (status.isError) return <Notice kind="error">{errorMessage(status.error)}</Notice>;
  const mfa = status.data;

  const submit = (action: () => void) => (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    action();
  };
  const passwordField = (
    <TextField
      label={t('account.mfa.confirmPassword')}
      type="password"
      autoComplete="current-password"
      value={password}
      onChange={(e) => setPassword(e.target.value)}
    />
  );
  const codeField = (
    <TextField
      label={t('account.mfa.code')}
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      value={code}
      onChange={(e) => setCode(e.target.value)}
    />
  );

  return (
    <section aria-label={t('account.mfa.title')} className={card}>
      <h2 className="text-lg font-semibold">{t('account.mfa.title')}</h2>
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {!mfa.available && <p className="text-sm text-slate-600">{t('account.mfa.unavailable')}</p>}

      {mfa.available && step === 'recovery' && (
        <div className="space-y-3">
          <h3 className="font-medium">{t('account.mfa.recoveryTitle')}</h3>
          <p className="text-sm text-slate-700">{t('account.mfa.recoveryWarning')}</p>
          <ul
            aria-label={t('account.mfa.recoveryTitle')}
            className="grid grid-cols-2 gap-2 font-mono text-sm"
          >
            {codes.map((c) => (
              <li key={c} className="rounded bg-slate-100 px-2 py-1">
                {c}
              </li>
            ))}
          </ul>
          <button
            type="button"
            className={primaryButton}
            onClick={() => {
              setCodes([]);
              reset();
            }}
          >
            {t('account.mfa.recoveryDone')}
          </button>
        </div>
      )}

      {mfa.available && step !== 'recovery' && !mfa.enabled && (
        <>
          <p className="text-sm text-slate-700">{t('account.mfa.off')}</p>
          {step === 'idle' && (
            <button type="button" className={primaryButton} onClick={() => setStep('password')}>
              {t('account.mfa.start')}
            </button>
          )}
          {step === 'password' && (
            <form onSubmit={submit(() => begin.mutate())} className="space-y-3" noValidate>
              {passwordField}
              <div className="flex gap-2">
                <button
                  type="submit"
                  className={primaryButton}
                  disabled={!password || begin.isPending}
                >
                  {t('account.mfa.begin')}
                </button>
                <button type="button" className={secondaryButton} onClick={reset}>
                  {t('account.mfa.cancel')}
                </button>
              </div>
            </form>
          )}
          {step === 'code' && setup && (
            <form onSubmit={submit(() => enable.mutate())} className="space-y-3" noValidate>
              <p className="text-sm text-slate-700">{t('account.mfa.scan')}</p>
              <p className="text-sm">
                {t('account.mfa.key')}:{' '}
                <code className="rounded bg-slate-100 px-1 font-mono">{setup.secret}</code>
              </p>
              <p className="break-all text-xs text-slate-600">
                {t('account.mfa.address')}:{' '}
                <a className="underline" href={setup.otpauthUri}>
                  {setup.otpauthUri}
                </a>
              </p>
              {codeField}
              <div className="flex gap-2">
                <button
                  type="submit"
                  className={primaryButton}
                  disabled={code.length !== 6 || enable.isPending}
                >
                  {t('account.mfa.enable')}
                </button>
                <button type="button" className={secondaryButton} onClick={reset}>
                  {t('account.mfa.cancel')}
                </button>
              </div>
            </form>
          )}
        </>
      )}

      {mfa.available && step !== 'recovery' && mfa.enabled && (
        <>
          <p className="text-sm text-slate-700">
            {t('account.mfa.on')} {t('account.mfa.codesLeft', { n: mfa.recoveryCodesLeft })}
          </p>
          {step === 'idle' && (
            <div className="flex flex-wrap gap-2">
              <button type="button" className={secondaryButton} onClick={() => setStep('replace')}>
                {t('account.mfa.replace')}
              </button>
              <button type="button" className={secondaryButton} onClick={() => setStep('disable')}>
                {t('account.mfa.disable')}
              </button>
            </div>
          )}
          {step === 'replace' && (
            <form onSubmit={submit(() => replace.mutate())} className="space-y-3" noValidate>
              <p className="text-sm text-slate-600">{t('account.mfa.replaceHint')}</p>
              {passwordField}
              {codeField}
              <div className="flex gap-2">
                <button
                  type="submit"
                  className={primaryButton}
                  disabled={!password || code.length !== 6 || replace.isPending}
                >
                  {t('account.mfa.replace')}
                </button>
                <button type="button" className={secondaryButton} onClick={reset}>
                  {t('account.mfa.cancel')}
                </button>
              </div>
            </form>
          )}
          {step === 'disable' && (
            <form onSubmit={submit(() => disable.mutate())} className="space-y-3" noValidate>
              <p className="text-sm text-slate-600">{t('account.mfa.disableHint')}</p>
              {passwordField}
              {codeField}
              <TextField
                label={t('account.mfa.recoveryField')}
                maxLength={14}
                value={recoveryField}
                onChange={(e) => setRecoveryField(e.target.value)}
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  className={primaryButton}
                  disabled={
                    !password ||
                    (code.length !== 6 && recoveryField.trim().length < 10) ||
                    disable.isPending
                  }
                >
                  {t('account.mfa.disable')}
                </button>
                <button type="button" className={secondaryButton} onClick={reset}>
                  {t('account.mfa.cancel')}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [message, setMessage] = useState<Message>(null);
  const change = useMutation({
    mutationFn: () =>
      api('/auth/change-password', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setMessage({ kind: 'ok', text: t('account.pw.changed') });
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  return (
    <section aria-label={t('account.pw.title')} className={card}>
      <h2 className="text-lg font-semibold">{t('account.pw.title')}</h2>
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <form
        noValidate
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          setMessage(null);
          change.mutate();
        }}
      >
        <TextField
          label={t('account.pw.current')}
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <TextField
          label={t('account.pw.new')}
          type="password"
          autoComplete="new-password"
          hint={t('auth.passwordHint')}
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <div>
          <button
            type="submit"
            className={primaryButton}
            disabled={!current || next.length < 12 || change.isPending}
          >
            {t('account.pw.change')}
          </button>
        </div>
      </form>
    </section>
  );
}

function Sessions() {
  const queryClient = useQueryClient();
  const sessions = useQuery({
    queryKey: ['account', 'sessions'],
    queryFn: () => api<SessionSummary[]>('/auth/sessions'),
  });
  const signOut = useMutation({
    mutationFn: (id: string) => api(`/auth/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['account', 'sessions'] }),
  });
  return (
    <section aria-label={t('account.sessions.title')} className={card}>
      <h2 className="text-lg font-semibold">{t('account.sessions.title')}</h2>
      {sessions.data?.length === 0 && (
        <p className="text-sm text-slate-600">{t('account.sessions.empty')}</p>
      )}
      <ul className="divide-y divide-slate-100 text-sm">
        {sessions.data?.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <span>
              {s.userAgent ?? '—'} · {s.ipAddress ?? '—'}
              <span className="block text-xs text-slate-600">
                {t('account.sessions.last', { time: new Date(s.lastUsedAt).toLocaleString() })}
              </span>
            </span>
            {s.current ? (
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">
                {t('account.sessions.current')}
              </span>
            ) : (
              <button
                type="button"
                className={secondaryButton}
                aria-label={`${t('account.sessions.signOut')} ${s.userAgent ?? s.id}`}
                disabled={signOut.isPending}
                onClick={() => signOut.mutate(s.id)}
              >
                {t('account.sessions.signOut')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
